import aiBuddyService from '@wlb/shared/api/services/aiBuddyService';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';

const PAGE_SIZE = 100;

let cachedAgentGuids: Set<string> | null = null;
let agentGuidsRequest: Promise<Set<string>> | null = null;

const normalize = (guid?: string | null): string | null =>
  normalizeUserGuid(guid)?.toLowerCase() ?? null;

const loadAgentGuids = async (): Promise<Set<string>> => {
  if (cachedAgentGuids) return cachedAgentGuids;
  if (agentGuidsRequest) return agentGuidsRequest;

  agentGuidsRequest = (async () => {
    const guids = new Set<string>();
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      const page = await aiBuddyService.getAIBuddies({
        pageNumber,
        pageSize: PAGE_SIZE,
      });
      page.data.forEach(agent => {
        const guid = normalize(agent.profileUserGuid ?? agent.agentGuid);
        if (guid) guids.add(guid);
      });
      hasMore = page.hasMore && page.data.length > 0;
      pageNumber += 1;
    }

    cachedAgentGuids = guids;
    return guids;
  })();

  try {
    return await agentGuidsRequest;
  } catch (error) {
    agentGuidsRequest = null;
    throw error;
  }
};

/**
 * AI agents use the Agent follow/chat relationship, never BuddyInvite.
 * The roster comes from the backend AgentsList endpoint and is cached so a
 * feed containing several reshares performs only one paginated lookup.
 */
export const isAiAgentProfileGuid = async (
  profileUserGuid?: string | null,
): Promise<boolean> => {
  const guid = normalize(profileUserGuid);
  if (!guid) return false;
  return (await loadAgentGuids()).has(guid);
};

export const resetAiAgentIdentityCacheForTests = (): void => {
  cachedAgentGuids = null;
  agentGuidsRequest = null;
};
